/**
 * Life4Earth V2 — Landing Page Content Model
 *
 * Centralised, editable content for the public landing page.
 *
 * Text fields support two modes:
 *   1. Direct text — admin types the value into `text` / `author` / `heading`.
 *      This is the primary path and what the admin console uses.
 *   2. i18n key — if `text` is empty, the landing page falls back to `t(textKey)`.
 *      This supports the original locale-file approach for multilingual sites.
 *
 * Images, colors, and structural flags live here so they can
 * be loaded from the backend CMS endpoint.
 */

// ── Types ────────────────────────────────────────────────────

export interface LandingQuote {
  /** Direct quote text (primary — used if non-empty) */
  text?: string;
  /** Direct attribution (primary — used if non-empty) */
  author?: string;
  /** i18n key fallback for quote body, e.g. "hero.quotes.1.text" */
  textKey: string;
  /** i18n key fallback for attribution, e.g. "hero.quotes.1.author" */
  authorKey: string;
}

export interface LandingPartner {
  name: string;
  logoUrl: string;
  websiteUrl?: string;
}

export interface LandingTeamMember {
  name: string;
  position: string;
  imageUrl: string;
  comment?: string;
}

export interface LandingRolloutBlock {
  /** Direct heading text (primary — used if non-empty) */
  heading?: string;
  /** Direct body text (primary — used if non-empty) */
  text?: string;
  /** i18n heading key fallback, e.g. "rollout.blocks.1.heading" */
  headingKey: string;
  /** i18n text key fallback, e.g. "rollout.blocks.1.text" */
  textKey: string;
  /** Pillar accent color */
  color: string;
}

export interface LandingContent {
  quotes: LandingQuote[];
  partners: LandingPartner[];
  team: LandingTeamMember[];
  rolloutBlocks: LandingRolloutBlock[];
  /** Video file base name (without resolution suffix) */
  videoBaseName: string;
  /** Hero background image fallback (mobile / no-video) */
  heroBgImage: string;
  /** Mission section image */
  missionImage: string;
  /** Rollout section feature image */
  rolloutImage: string;
}

// ── Default content ──────────────────────────────────────────

export const DEFAULT_LANDING_CONTENT: LandingContent = {
  quotes: [
    {
      text: 'The Earth is what we all have in common.',
      author: 'Wendell Berry',
      textKey: 'hero.quotes.1.text',
      authorKey: 'hero.quotes.1.author',
    },
    {
      text: 'We do not inherit the earth from our ancestors — we borrow it from our children.',
      author: 'Native American Proverb',
      textKey: 'hero.quotes.2.text',
      authorKey: 'hero.quotes.2.author',
    },
    {
      text: 'The greatest threat to our planet is the belief that someone else will save it.',
      author: 'Robert Swan',
      textKey: 'hero.quotes.3.text',
      authorKey: 'hero.quotes.3.author',
    },
  ],

  partners: [
    // Phase 1: placeholders — replace with real logos via admin or API
    { name: 'Partner Placeholder 1', logoUrl: '', websiteUrl: '' },
    { name: 'Partner Placeholder 2', logoUrl: '', websiteUrl: '' },
    { name: 'Partner Placeholder 3', logoUrl: '', websiteUrl: '' },
  ],

  team: [
    {
      name: 'Skip Davis',
      position: 'Founder & CEO',
      imageUrl: '',
      comment: 'Building a platform that connects the world\'s problems with their solutions.',
    },
    {
      name: 'Martha Court',
      position: 'Co-Founder',
      imageUrl: '',
    },
    {
      name: 'Tommy Nilsson',
      position: 'Technology',
      imageUrl: '',
    },
    {
      name: 'Dave Stockwell',
      position: 'Advisory',
      imageUrl: '',
    },
    {
      name: 'Tom Schultz',
      position: 'Advisory',
      imageUrl: '',
    },
  ],

  rolloutBlocks: [
    {
      heading: 'Problems',
      text: 'Identify and catalog the challenges facing communities worldwide.',
      headingKey: 'rollout.blocks.1.heading',
      textKey: 'rollout.blocks.1.text',
      color: '#D53541',
    },
    {
      heading: 'Solutions',
      text: 'Discover proven approaches and innovations that work.',
      headingKey: 'rollout.blocks.2.heading',
      textKey: 'rollout.blocks.2.text',
      color: '#22C55E',
    },
    {
      heading: 'Projects',
      text: 'Organize and track initiatives that turn solutions into action.',
      headingKey: 'rollout.blocks.3.heading',
      textKey: 'rollout.blocks.3.text',
      color: '#A855F7',
    },
    {
      heading: 'Enablers',
      text: 'Connect with the people and organizations who can help.',
      headingKey: 'rollout.blocks.4.heading',
      textKey: 'rollout.blocks.4.text',
      color: '#3B82F6',
    },
  ],

  videoBaseName: 'landing_v1',
  heroBgImage: '/content/images/landing-page/main-bg.jpg',
  missionImage: '/content/images/landing-page/johannes-andersson-UCd78vfC8vU-unsplash.jpg',
  rolloutImage: '/content/images/landing-page/adam-kool-ndN00KmbJ1c-unsplash.jpg',
};
